import React from "react";
import { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { isEditableElement } from "@/lib/editableElement";
import { SimpleDialog } from "@/main/react/Dialog/SimpleDialog";

/**
 * Thread keyboard shortcuts (FI-586, round 37 `1c` A4). Mechanism: `designs/implementation-map/discussions.md` §4.3.
 *
 * <p>`j` / `k` focus the next / previous opening-thread card and scroll it to the top, `r` asks the focused card to
 * open its reply composer, `?` opens {@link ThreadShortcutsHelpDialog}. ONE document listener per mounted list.
 */

/** Dispatched on the focused card by `r`; the card answers it with its own reply-open path. */
export const THREAD_SHORTCUT_REPLY_EVENT = "seavien:thread-shortcut-reply";
export const OPENING_THREAD_CARD_SELECTOR = '[data-playwright-id="discussion-opening-thought-card"]';

const OPEN_DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

function shouldIgnore(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) {
        return true;
    }
    if (isEditableElement(document.activeElement)) {
        return true;
    }
    if (event.target instanceof Element && isEditableElement(event.target)) {
        return true;
    }
    // A dialog owns the keyboard while it is open (the reply sheet, a confirm, this list's own help sheet).
    return document.querySelector(OPEN_DIALOG_SELECTOR) != null;
}

export function useThreadKeyboardShortcuts(
    containerRef: React.RefObject<HTMLElement | null>,
    options: { enabled?: boolean } = {},
): { helpOpen: boolean; setHelpOpen: (open: boolean) => void } {
    const enabled = options.enabled ?? true;
    const [helpOpen, setHelpOpen] = React.useState(false);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        const cards = (): HTMLElement[] => {
            const container = containerRef.current;
            return container ? Array.from(container.querySelectorAll<HTMLElement>(OPENING_THREAD_CARD_SELECTOR)) : [];
        };
        const focusedCard = (): HTMLElement | null => {
            const active = document.activeElement;
            const card = active instanceof Element ? active.closest<HTMLElement>(OPENING_THREAD_CARD_SELECTOR) : null;
            return card && containerRef.current?.contains(card) ? card : null;
        };
        const move = (step: 1 | -1) => {
            const all = cards();
            if (all.length === 0) {
                return false;
            }
            const current = focusedCard();
            const index = current ? all.indexOf(current) : -1;
            // Nothing focused yet: either key starts at the first thread. Clamped at both ends, never wraps.
            const nextIndex = index < 0 ? 0 : Math.min(all.length - 1, Math.max(0, index + step));
            const target = all[nextIndex];
            if (!target.hasAttribute("tabindex")) {
                target.setAttribute("tabindex", "-1");
            }
            target.focus({ preventScroll: true });
            target.scrollIntoView({ block: "start" });
            return true;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (!["j", "k", "r", "?"].includes(event.key) || shouldIgnore(event)) {
                return;
            }
            if (event.key === "?") {
                event.preventDefault();
                setHelpOpen(true);
                return;
            }
            if (event.key === "r") {
                const card = focusedCard();
                if (card) {
                    event.preventDefault();
                    card.dispatchEvent(new CustomEvent(THREAD_SHORTCUT_REPLY_EVENT));
                }
                return;
            }
            if (move(event.key === "j" ? 1 : -1)) {
                event.preventDefault();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [containerRef, enabled]);

    return { helpOpen, setHelpOpen };
}

/** Key glyphs are not localized; only the descriptions are. */
const SHORTCUT_ROWS: ReadonlyArray<{ glyph: string; labelKey: LocalizationKey }> = [
    { glyph: "j", labelKey: "discussion.shortcuts.next" },
    { glyph: "k", labelKey: "discussion.shortcuts.previous" },
    { glyph: "r", labelKey: "discussion.shortcuts.reply" },
    { glyph: "?", labelKey: "discussion.shortcuts.help" },
];

export function ThreadShortcutsHelpDialog(props: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    localizer: Localizer;
}) {
    const { open, onOpenChange, localizer } = props;
    if (!open) {
        return null;
    }
    return (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={localizer("discussion.shortcuts.title")}
            width="sm"
            dataPlaywrightId="discussion-shortcuts-help"
        >
            <dl className="flex flex-col gap-3 text-sm">
                {SHORTCUT_ROWS.map((row) => (
                    <div key={row.glyph} className="flex items-center gap-3">
                        <dt>
                            <kbd className="inline-flex min-w-7 items-center justify-center rounded-md border border-border px-2 py-0.5 font-mono text-xs text-foreground">
                                {row.glyph}
                            </kbd>
                        </dt>
                        <dd className="text-muted-foreground">{localizer(row.labelKey)}</dd>
                    </div>
                ))}
            </dl>
        </SimpleDialog>
    );
}
